import React from "react";
import { ConfidencePill, DataState, IcArrow, IcCheck, IcClock, IcDoc, IcHandshake, IcMail, IcPin, IcRefresh, IcSearch, IcSend, IcX, StatusPill } from "./shared.jsx";
import { FooterNav, StepHeader } from "./steps_early.jsx";
// ============ BUYER-BROKER AGREEMENT (BBA) TRANSFERS ============
// Active buyer representation agreements live as DEAL CARDS in Followup Boss
// (Buyer pipeline). The brokerage holds one org-level FUB API key; we query the
// agent's deals where a Buyer-Broker Exclusive Employment Agreement is attached
// and active, then surface them for selective transfer. Each transfer executes
// EveryPlace Form EP·BBT — "Authorization to Transfer Buyer-Broker Agreement" —
// signed by the Buyer(s), the Current Broker of Record, and the Receiving Broker.
const { useState: _b_uS, useEffect: _b_uE } = React;

const BBA_RECEIVING_BROKER = "EveryPlace";
const BBA_RECEIVING_SIGNER = "Amber Valentine";

// Sample deal cards normalized from FUB GET /v1/deals?pipeline=Buyer&hasAgreement=true
const BBAS = [
  {
    id: "B1",
    fubDealId: "FUB-DC-44021",
    stage: "Active Buyer · Touring",
    buyer:  { name: "Jordan & Mia Petrakis", email: "jpetrakis@gmail.com", phone: "(480) 555-0188" },
    interest: "Scottsdale 85254 · up to $750K",
    commencement: "Mar 12, 2026",
    expiration: "Sep 12, 2026",
    fubMatch: { confidence: "high", pct: 98 },
    selected: false,
    transferStatus: "found",
  },
  {
    id: "B2",
    fubDealId: "FUB-DC-44088",
    stage: "Active Buyer · Offer Prep",
    buyer:  { name: "Trevor Nakamura", email: "t.nakamura@outlook.com", phone: "(602) 555-0131" },
    interest: "Tempe / Mesa · $400–500K",
    commencement: "Apr 02, 2026",
    expiration: "Oct 02, 2026",
    fubMatch: { confidence: "high", pct: 95 },
    selected: false,
    transferStatus: "found",
  },
  {
    id: "B3",
    fubDealId: "FUB-DC-43977",
    stage: "Active Buyer · Nurture",
    buyer:  { name: "Gabriela Ortiz", email: "", phone: "(623) 555-0174" },
    interest: "Gilbert · up to $625K",
    commencement: "Feb 20, 2026",
    expiration: "Aug 20, 2026",
    fubMatch: { confidence: "med", pct: 79 },
    selected: false,
    transferStatus: "found",
  },
  {
    id: "B4",
    fubDealId: "FUB-DC-44115",
    stage: "Active Buyer · Touring",
    buyer:  { name: "The Hendricks Family", email: "hendricks.az@icloud.com", phone: "(480) 555-0209" },
    interest: "Chandler · $800K+",
    commencement: "Jan 28, 2026",
    expiration: "Jul 28, 2026",
    fubMatch: { confidence: "high", pct: 97 },
    selected: false,
    transferStatus: "found",
  },
  {
    id: "B5",
    fubDealId: "FUB-DC-44140",
    stage: "Active Buyer · Nurture",
    buyer:  { name: "Aisha Bello", email: "", phone: "(602) 555-0150" },
    interest: "Phoenix · $350–425K",
    commencement: "Apr 25, 2026",
    expiration: "Oct 25, 2026",
    fubMatch: { confidence: "low", pct: 58 },
    selected: false,
    transferStatus: "found",
  },
];

function StepBBA({ onNext, onBack, bbas, setBbas, agent, openSign, loading, error, onReload }) {
  // phase: "ask" (do you have any?) | "scanning" | "results" | "none"
  const hasData = bbas.some(b => b.transferStatus !== "found" || b.selected) || window.__bbaScanned;
  const [phase, setPhase] = _b_uS(hasData ? "results" : "ask");
  const [scanning, setScanning] = _b_uS(false);
  const [query, setQuery] = _b_uS("");
  const [confirmOpen, setConfirmOpen] = _b_uS(false);
  const [lastSync, setLastSync] = _b_uS("just now");

  const selected = bbas.filter(b => b.selected);
  const pending = selected.filter(b => b.transferStatus !== "transferred");
  const anyInFlight = bbas.some(b => ["sent_parties", "countersign"].includes(b.transferStatus));
  const canContinue = pending.length === 0;

  // Scan re-hits GET /v1/bbas (FUB buyer-pipeline) via the App-provided reloader.
  async function runScan() {
    setPhase("scanning");
    setScanning(true);
    window.__bbaScanned = true;
    try { await onReload?.(); }
    finally { setScanning(false); setPhase("results"); }
  }

  function toggle(id) {
    setBbas(bbas.map(b => b.id === id && b.transferStatus === "found"
      ? { ...b, selected: !b.selected } : b));
  }

  const visible = bbas.filter(b =>
    !query.trim() ||
    (b.buyer.name || "").toLowerCase().includes(query.toLowerCase()) ||
    b.fubDealId.toLowerCase().includes(query.toLowerCase()) ||
    b.interest.toLowerCase().includes(query.toLowerCase())
  );

  // ---------- ASK GATE ----------
  if (phase === "ask") {
    return (
      <div style={{maxWidth: 1100}}>
        <StepHeader eyebrow="Step 08 / 09" title="Transfer your buyer-broker agreements."
          subtitle={<>Active buyer representation agreements transfer with you. If you have signed Buyer-Broker Exclusive Employment Agreements in progress, we'll pull them from your <strong>Followup Boss</strong> deal cards so each can be re-papered to {BBA_RECEIVING_BROKER}.</>}/>

        <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>Before we scan</div>
        <h3 style={{font: "300 30px/1.15 var(--ep-font-display)", margin: "8px 0 22px", maxWidth: "20ch"}}>
          Do you have buyer-broker agreements to transfer?
        </h3>

        <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 760, marginBottom: 40}}>
          <div className="ep-card" onClick={runScan}
            style={{padding: 26, cursor: "pointer", display: "flex", flexDirection: "column", gap: 12}}>
            <div style={{width: 46, height: 46, borderRadius: 2, background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", display: "flex", alignItems: "center", justifyContent: "center"}}>
              <IcHandshake size={22}/>
            </div>
            <div style={{font: "300 24px/1.1 var(--ep-font-display)"}}>Yes, I have agreements</div>
            <div style={{fontSize: 13.5, color: "var(--ep-fg-muted)", lineHeight: 1.55, flex: 1}}>
              Search my Followup Boss deal cards for active buyer-broker agreements and prepare them for transfer.
            </div>
            <div className="ep-btn primary sm" style={{alignSelf: "flex-start"}}>
              <IcSearch size={12}/> Scan Followup Boss
            </div>
          </div>

          <div className="ep-card" onClick={() => setPhase("none")}
            style={{padding: 26, cursor: "pointer", display: "flex", flexDirection: "column", gap: 12}}>
            <div style={{width: 46, height: 46, borderRadius: 2, background: "var(--ep-origin-10)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center"}}>
              <IcX size={22}/>
            </div>
            <div style={{font: "300 24px/1.1 var(--ep-font-display)"}}>No, none to transfer</div>
            <div style={{fontSize: 13.5, color: "var(--ep-fg-muted)", lineHeight: 1.55, flex: 1}}>
              I have no active buyer-broker agreements, or I'll handle them later. Skip ahead to the license transfer.
            </div>
            <div className="ep-btn ghost sm" style={{alignSelf: "flex-start"}}>Skip this step</div>
          </div>
        </div>

        <FooterNav onBack={onBack} onNext={onNext} nextDisabled nextLabel="Choose an option above"/>
      </div>
    );
  }

  // ---------- NONE ----------
  if (phase === "none") {
    return (
      <div style={{maxWidth: 1100}}>
        <StepHeader eyebrow="Step 08 / 09" title="No buyer-broker agreements to transfer."
          subtitle="You can return to this step any time before submitting your license transfer if an agreement comes up."/>
        <div style={{padding: 44, textAlign: "center", border: "1px dashed var(--ep-border-strong)", background: "#FFFAF0", borderRadius: 2, marginBottom: 40}}>
          <div style={{width: 56, height: 56, borderRadius: 999, background: "var(--ep-avocado)", color: "var(--ep-origin)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16}}>
            <IcCheck size={28}/>
          </div>
          <h3 style={{font: "300 30px/1.15 var(--ep-font-display)", margin: "0 0 8px"}}>Nothing to do here.</h3>
          <p style={{color: "var(--ep-fg-muted)", maxWidth: "46ch", margin: "0 auto 22px"}}>
            We won't transfer any buyer agreements. Changed your mind?
          </p>
          <button className="ep-btn secondary sm" onClick={() => setPhase("ask")}>Actually, let me scan Followup Boss</button>
        </div>
        <FooterNav onBack={onBack} onNext={onNext} nextLabel="Review & Finish"/>
      </div>
    );
  }

  // ---------- SCANNING ----------
  if (phase === "scanning") {
    return (
      <div style={{maxWidth: 1100}}>
        <StepHeader eyebrow="Step 08 / 09" title="Searching Followup Boss…"
          subtitle="Scanning your deal cards for active Buyer-Broker Exclusive Employment Agreements."/>
        <div style={{padding: "60px 44px", textAlign: "center", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", borderRadius: 2}}>
          <div className="bba-spinner" style={{margin: "0 auto 24px"}}/>
          <div style={{font: "300 26px/1.2 var(--ep-font-display)", color: "var(--ep-saguaro-blossom)", marginBottom: 8}}>
            Reading buyer pipeline deal cards
          </div>
          <div style={{color: "rgba(255,245,228,0.7)", fontSize: 14}}>
            Matching attached agreements to Followup Boss contacts…
          </div>
        </div>
        <style>{`
          .bba-spinner { width: 46px; height: 46px; border-radius: 999px;
            border: 3px solid rgba(255,245,228,0.2); border-top-color: var(--ep-high-noon);
            animation: bbaSpin 0.8s linear infinite; }
          @keyframes bbaSpin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // ---------- RESULTS ----------
  return (
    <div style={{maxWidth: 1100}}>
      <StepHeader eyebrow="Step 05 / 06" title="Select the agreements to transfer."
        subtitle={<>We scan your <strong>Followup Boss</strong> deal cards for active buyer-broker agreements. Check the ones moving with you to {BBA_RECEIVING_BROKER}, confirm the details, and send each on Form EP·BBT for signature.</>}/>

      <DataState loading={loading} error={error} onReload={onReload} noun="agreements"/>

      {/* Sync bar */}
      <div style={{display: "flex", gap: 14, alignItems: "center", marginBottom: 24, padding: "16px 20px", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
        <IcSearch size={18}/>
        <input className="ep-input" style={{border: 0, background: "transparent", padding: 0, fontSize: 15}}
          placeholder="Search buyer, deal card #, or interest area"
          value={query} onChange={e => setQuery(e.target.value)}/>
        <div style={{display: "flex", alignItems: "center", gap: 10, color: "var(--ep-fg-muted)", fontSize: 13, whiteSpace: "nowrap"}}>
          <IcClock size={14}/> Synced: {lastSync}
        </div>
        <button className="ep-btn ghost sm" onClick={runScan} disabled={scanning}>
          <IcRefresh size={12}/> Re-scan FUB
        </button>
      </div>

      {/* Counts */}
      <div className="ep-strip-mobile" style={{display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, marginBottom: 32, background: "var(--ep-border)", border: "1px solid var(--ep-border)"}}>
        {[
          {n: bbas.length, l: "Agreements Found"},
          {n: selected.length, l: "Selected"},
          {n: bbas.filter(b => ["sent_parties","countersign"].includes(b.transferStatus)).length, l: "Out for Signature"},
          {n: bbas.filter(b => b.transferStatus === "transferred").length, l: "Transferred"},
        ].map((s,i) => (
          <div key={i} style={{background: "var(--ep-bg-elevated)", padding: "20px 22px"}}>
            <div style={{font: "300 42px/1 var(--ep-font-display)", color: "var(--ep-origin)"}}>{s.n}</div>
            <div className="ep-caption" style={{marginTop: 6}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* BBA rows */}
      <div className="vstack" style={{gap: 14, marginBottom: 24}}>
        {visible.map(b => (
          <BBARow key={b.id} bba={b} onToggle={() => toggle(b.id)}/>
        ))}
        {visible.length === 0 && (
          <div style={{padding: 40, textAlign: "center", color: "var(--ep-fg-muted)", border: "1px dashed var(--ep-border)", background: "var(--ep-bg-elevated)"}}>
            No agreements match that search.
          </div>
        )}
      </div>

      {/* Selection action bar */}
      {pending.length > 0 && (
        <div style={{position: "sticky", bottom: 16, zIndex: 30, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "16px 24px", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", borderRadius: 4, boxShadow: "0 12px 40px rgba(71,60,22,0.3)", marginBottom: 32}}>
          <div style={{display: "flex", alignItems: "center", gap: 12}}>
            <div style={{width: 34, height: 34, borderRadius: 999, background: "var(--ep-high-noon)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center", font: "700 14px/1 var(--ep-font-body)"}}>{pending.length}</div>
            <div>
              <div style={{fontSize: 14, fontWeight: 600}}>{pending.length} agreement{pending.length > 1 ? "s" : ""} selected to transfer</div>
              <div style={{fontSize: 12, color: "rgba(255,245,228,0.7)"}}>Confirm the details before sending for signature.</div>
            </div>
          </div>
          <button className="ep-btn primary sm" onClick={() => setConfirmOpen(true)}>
            Review &amp; Send for Signature <IcArrow size={12}/>
          </button>
        </div>
      )}

      {confirmOpen && (
        <BBAConfirmModal bbas={selected.filter(b => b.transferStatus === "found")} agent={agent}
          onClose={() => setConfirmOpen(false)}
          onUpdate={(updated) => setBbas(bbas.map(x => x.id === updated.id ? updated : x))}
          onSend={(toSend) => {
            const ids = toSend.map(b => b.id);
            setConfirmOpen(false);
            launchBBASign(toSend, agent, ids, setBbas);
          }}/>
      )}

      <FooterNav onBack={onBack} onNext={onNext}
        nextDisabled={!canContinue}
        nextLabel={canContinue ? "Review & Finish" : (anyInFlight ? "Waiting on signatures…" : "Send selected for signature first")}/>

      {!canContinue && (
        <div style={{textAlign: "right", marginTop: 12}}>
          <span className="ep-link" style={{fontSize: 12, color: "var(--ep-fg-muted)"}}
            onClick={onNext}>
            Skip for now (handle these later)
          </span>
        </div>
      )}
    </div>
  );
}

async function launchBBASign(toSend, agent, ids, setBbas) {
  // Fire one POST /api/v1/bba-transfer per agreement sequentially.
  // Update transferStatus as each resolves so the UI tracks progress.
  for (const b of toSend) {
    // Mark "in flight" immediately so the row shows "Out for signature" spinner
    setBbas(curr => curr.map(x => x.id === b.id ? { ...x, transferStatus: "sent_parties", transferError: null } : x));
    try {
      const resp = await fetch("/api/v1/bba-transfer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bba: {
            buyer:        b.buyer,
            buyer2:       b.buyer2 || null,
            commencement: b.commencement,
            expiration:   b.expiration,
          },
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.error("[bba-transfer] error", b.id, json);
        setBbas(curr => curr.map(x => x.id === b.id
          ? { ...x, transferStatus: "found", transferError: json?.error || "Send failed — try again" }
          : x));
        continue;
      }
      setBbas(curr => curr.map(x => x.id === b.id
        ? { ...x, transferStatus: "countersign", documentId: json.documentId, transferError: null }
        : x));
    } catch (err) {
      console.error("[bba-transfer] network error", b.id, err);
      setBbas(curr => curr.map(x => x.id === b.id
        ? { ...x, transferStatus: "found", transferError: "Network error — try again" }
        : x));
    }
  }
}

function bbaMeta(b, agent) {
  return {
    buyer: b.buyer.name,
    currentBroker: agent.currentBroker,
    salesperson: agent.name,
    commencement: b.commencement,
    expiration: b.expiration,
    interest: b.interest,
    receivingBroker: BBA_RECEIVING_BROKER,
    receivingSigner: BBA_RECEIVING_SIGNER,
    effective: new Date().toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"}),
  };
}

function BBARow({ bba, onToggle }) {
  const b = bba;
  const isFound = b.transferStatus === "found";
  const needsReview = !b.buyer.email;
  return (
    <div className="listing-card ep-stack-mobile" style={{gridTemplateColumns: "auto auto 1fr auto", alignItems: "center",
      borderColor: b.selected ? "var(--ep-sunset)" : "var(--ep-border)",
      background: b.selected ? "#FFFAF0" : "var(--ep-bg-elevated)"}}>
      {/* Checkbox */}
      <button onClick={isFound ? onToggle : undefined}
        aria-label="Select agreement"
        style={{width: 26, height: 26, borderRadius: 4, flexShrink: 0, cursor: isFound ? "pointer" : "default",
          border: `1.5px solid ${b.selected ? "var(--ep-sunset)" : "var(--ep-border-strong)"}`,
          background: b.selected ? "var(--ep-sunset)" : "transparent",
          color: "var(--ep-saguaro-blossom)", display: "flex", alignItems: "center", justifyContent: "center",
          opacity: isFound ? 1 : 0.4, padding: 0}}>
        {b.selected ? <IcCheck size={15}/> : (b.transferStatus === "transferred" ? <IcCheck size={15}/> : null)}
      </button>

      <div style={{width: 56, height: 56, borderRadius: 2, background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
        <IcHandshake size={24}/>
      </div>

      <div style={{display: "flex", flexDirection: "column", gap: 8, minWidth: 0}}>
        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          <span className="ep-caption" style={{color: "var(--ep-fg-muted)"}}>Deal card {b.fubDealId}</span>
          <StatusPill status={b.transferStatus === "found" ? "draft" : b.transferStatus}/>
          <ConfidencePill confidence={b.fubMatch.confidence} pct={b.fubMatch.pct}/>
        </div>
        <div style={{font: "300 24px/1.1 var(--ep-font-display)", letterSpacing: "-0.01em"}}>{b.buyer.name}</div>
        <div style={{display: "flex", gap: 16, color: "var(--ep-fg-muted)", fontSize: 13, flexWrap: "wrap"}}>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcPin size={13}/> {b.interest}</span>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcDoc size={13}/> {b.stage}</span>
        </div>
        <div style={{fontSize: 13, color: "var(--ep-fg-muted)", marginTop: 2}}>
          Agreement <strong style={{color: "var(--ep-origin)", fontWeight: 600}}>{b.commencement}</strong> – <strong style={{color: "var(--ep-origin)", fontWeight: 600}}>{b.expiration}</strong>
          {b.buyer.email && <> · {b.buyer.email}</>}
          {needsReview && isFound && <span style={{color: "var(--ep-rosewood)", marginLeft: 6}}>· Add buyer email before sending</span>}
          {b.transferError && <span style={{color: "var(--ep-rosewood)", marginLeft: 6}}>· {b.transferError}</span>}
        </div>
      </div>

      <div style={{display: "flex", flexDirection: "column", gap: 8, justifyContent: "center", alignItems: "flex-end"}}>
        {isFound ? (
          <button className={`ep-btn ${b.selected ? "primary" : "secondary"} sm`} onClick={onToggle}>
            {b.selected ? <><IcCheck size={12}/> Selected</> : "Select to transfer"}
          </button>
        ) : (
          <span className="ep-badge complete" style={{padding: "8px 12px"}}>
            {b.transferStatus === "transferred" ? "Transferred" : "Out for signature"}
          </span>
        )}
      </div>
    </div>
  );
}

function BBAConfirmModal({ bbas, agent, onClose, onUpdate, onSend }) {
  const [local, setLocal] = _b_uS(bbas);
  _b_uE(() => { setLocal(bbas); }, []);

  const updBuyer = (id, patch) => setLocal(local.map(b => b.id === id
    ? {...b, buyer: {...b.buyer, ...patch}} : b));

  const updBuyer2 = (id, patch) => setLocal(local.map(b => b.id === id
    ? {...b, buyer2: patch === null ? null : {...(b.buyer2 || {}), ...patch}} : b));

  const allValid = local.length > 0 && local.every(b =>
    b.buyer.name.trim() && b.buyer.email.trim() &&
    (!b.buyer2 || (b.buyer2.name?.trim() && b.buyer2.email?.trim())));

  return (
    <div className="modal-backdrop" onClick={(ev) => ev.target.classList.contains("modal-backdrop") && onClose()}>
      <div style={{width: "min(940px, 95vw)", maxHeight: "92vh", background: "var(--ep-bg-elevated)", borderRadius: 4, display: "flex", flexDirection: "column", animation: "slideUp 260ms var(--ep-ease)"}}>
        {/* Header */}
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid var(--ep-border)"}}>
          <div>
            <div className="ep-caption" style={{color: "var(--ep-sunset)"}}>Form EP·BBT · Confirm Before Sending</div>
            <div style={{font: "300 24px/1.1 var(--ep-font-display)", marginTop: 4}}>
              {local.length} buyer-broker agreement{local.length > 1 ? "s" : ""}
            </div>
          </div>
          <button className="ep-btn ghost sm" onClick={onClose}><IcX/></button>
        </div>

        {/* Body */}
        <div className="scroll-y" style={{padding: 28, display: "flex", flexDirection: "column", gap: 20}}>
          <div style={{padding: "12px 16px", background: "rgba(204,207,129,0.18)", border: "1px solid var(--ep-avocado)", borderRadius: 2, fontSize: 13, color: "var(--ep-origin)"}}>
            <strong>From → To.</strong> Each agreement transfers from <strong>{agent.currentBroker}</strong> to <strong>{BBA_RECEIVING_BROKER}</strong>. You remain the assigned salesperson. Transfer fee: <strong>$0.00 · No Fee</strong>.
          </div>

          {local.map(b => (
            <div key={b.id} style={{border: "1px solid var(--ep-border)", borderRadius: 2, padding: 20}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14}}>
                <div style={{font: "300 22px/1.1 var(--ep-font-display)"}}>{b.buyer.name || "Unnamed buyer"}</div>
                <ConfidencePill confidence={b.fubMatch.confidence} pct={b.fubMatch.pct}/>
              </div>

              {/* Pulled FUB facts */}
              <div className="ep-strip-mobile" style={{display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, background: "var(--ep-border)", border: "1px solid var(--ep-border)", marginBottom: 16}}>
                {[
                  ["Deal Card", b.fubDealId],
                  ["Current Broker", agent.currentBroker],
                  ["Commencement", b.commencement],
                  ["Expiration", b.expiration],
                ].map(([k,v]) => (
                  <div key={k} style={{background: "var(--ep-bg-elevated)", padding: "10px 12px"}}>
                    <div style={{fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ep-fg-muted)", fontWeight: 600, marginBottom: 4}}>{k}</div>
                    <div style={{fontSize: 13, color: "var(--ep-origin)"}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Editable buyer contact */}
              <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1.4fr 1.4fr 1fr", gap: 12}}>
                <label className="ep-field" style={{margin: 0}}>
                  <span className="ep-eyebrow">Buyer / Tenant Name(s)</span>
                  <input className="ep-input" value={b.buyer.name} onChange={e => updBuyer(b.id, {name: e.target.value})}/>
                </label>
                <label className="ep-field" style={{margin: 0}}>
                  <span className="ep-eyebrow">Buyer Email</span>
                  <input className="ep-input" value={b.buyer.email} onChange={e => updBuyer(b.id, {email: e.target.value})}
                    placeholder="Required" style={{borderColor: b.buyer.email ? "var(--ep-border)" : "var(--ep-high-noon)"}}/>
                </label>
                <label className="ep-field" style={{margin: 0}}>
                  <span className="ep-eyebrow">Buyer Phone</span>
                  <input className="ep-input" value={b.buyer.phone} onChange={e => updBuyer(b.id, {phone: e.target.value})}/>
                </label>
              </div>

              {/* Buyer 2 — optional toggle */}
              {!b.buyer2 ? (
                <button className="ep-btn ghost sm" style={{alignSelf: "flex-start", marginTop: 8}}
                  onClick={() => updBuyer2(b.id, {name: "", email: ""})}>
                  + Add a second buyer
                </button>
              ) : (
                <div style={{marginTop: 8, padding: "14px 16px", background: "var(--ep-bg)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10}}>
                    <span className="ep-eyebrow" style={{color: "var(--ep-fg-muted)"}}>Second Buyer</span>
                    <button className="ep-btn ghost sm" style={{padding: "2px 8px", fontSize: 11}}
                      onClick={() => updBuyer2(b.id, null)}>Remove</button>
                  </div>
                  <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1.4fr 1.4fr", gap: 12}}>
                    <label className="ep-field" style={{margin: 0}}>
                      <span className="ep-eyebrow">Name</span>
                      <input className="ep-input" value={b.buyer2.name || ""} onChange={e => updBuyer2(b.id, {name: e.target.value})}
                        placeholder="Required"/>
                    </label>
                    <label className="ep-field" style={{margin: 0}}>
                      <span className="ep-eyebrow">Email</span>
                      <input className="ep-input" value={b.buyer2.email || ""} onChange={e => updBuyer2(b.id, {email: e.target.value})}
                        placeholder="Required" style={{borderColor: b.buyer2.email ? "var(--ep-border)" : "var(--ep-high-noon)"}}/>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Routing */}
          <div>
            <div className="ep-eyebrow">Signature Routing (per agreement)</div>
            <div style={{padding: 16, background: "#FFFAF0", border: "1px solid var(--ep-border)", borderRadius: 2, fontSize: 13, color: "var(--ep-fg)"}}>
              <div style={{marginBottom: 8}}><strong>1.</strong> Buyer <span style={{color:"var(--ep-fg-muted)"}}>— signs the transfer authorization</span></div>
              {local.some(b => b.buyer2) && (
                <div style={{marginBottom: 8}}><strong>2.</strong> Second Buyer <span style={{color:"var(--ep-fg-muted)"}}>— co-signs (when applicable)</span></div>
              )}
              <div style={{marginBottom: 8}}><strong>{local.some(b => b.buyer2) ? "3" : "2"}.</strong> {BBA_RECEIVING_SIGNER} · {BBA_RECEIVING_BROKER} <span style={{color:"var(--ep-fg-muted)"}}>(Receiving Broker — accept)</span></div>
              <div style={{marginBottom: 8}}><strong>{local.some(b => b.buyer2) ? "4" : "3"}.</strong> {agent.currentBroker} <span style={{color:"var(--ep-fg-muted)"}}>(Current Broker — release)</span></div>
              <div style={{paddingTop: 8, borderTop: "1px solid var(--ep-divider)", color: "var(--ep-fg-muted)", display: "flex", alignItems: "center", gap: 6}}><IcMail size={13}/> Executed copies routed to {agent.email}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{display: "flex", justifyContent: "space-between", padding: "18px 28px", borderTop: "1px solid var(--ep-border)", background: "#FFFAF0"}}>
          <button className="ep-btn ghost" onClick={onClose}>Cancel</button>
          <div className="hstack">
            <button className="ep-btn secondary sm" onClick={() => { local.forEach(onUpdate); onClose(); }}>Save Edits</button>
            <button className="ep-btn primary sm" disabled={!allValid}
              onClick={() => { local.forEach(onUpdate); onSend(local); }}>
              <IcSend/> Send {local.length} for Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { StepBBA, BBAS };
